import { useEffect, useState } from 'react';

import { Button } from '../../design-system/button.js';
import { FieldError, FieldLabel, Input } from '../../design-system/form.js';
import {
  ONSET_AUDIT_DURATION_CEILING_SECONDS,
  formatDurationInput,
  parseDurationInput,
  type DurationRange,
} from './onset-audit-model.js';

type DurationBound = 'minimum' | 'maximum';

function DurationControl({
  bound,
  onChange,
  range,
}: {
  bound: DurationBound;
  onChange: (range: DurationRange) => void;
  range: DurationRange;
}) {
  const value = range[bound];
  const label = bound === 'minimum' ? 'Minimum' : 'Maximum';
  const [draft, setDraft] = useState(() => formatDurationInput(value));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setDraft(formatDurationInput(value)), [value]);

  const updateFromText = (nextDraft: string) => {
    setDraft(nextDraft);
    const nextValue = parseDurationInput(nextDraft);
    if (nextValue === null) {
      setError('Use m:ss.');
      return;
    }
    if (nextValue > ONSET_AUDIT_DURATION_CEILING_SECONDS) {
      setError(`Use ${formatDurationInput(ONSET_AUDIT_DURATION_CEILING_SECONDS)} or less.`);
      return;
    }
    if (bound === 'minimum' && nextValue > range.maximum) {
      setError('Minimum cannot exceed maximum.');
      return;
    }
    if (bound === 'maximum' && nextValue < range.minimum) {
      setError('Maximum cannot be below minimum.');
      return;
    }
    setError(null);
    onChange({ ...range, [bound]: nextValue });
  };

  const updateFromSlider = (nextValue: number) => {
    setError(null);
    onChange({ ...range, [bound]: nextValue });
  };

  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-3">
        <FieldLabel htmlFor={`duration-${bound}-range`}>{label}</FieldLabel>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_5.75rem] items-center gap-3">
        <input
          aria-label={`${label} duration slider`}
          className="h-11 w-full cursor-pointer accent-[var(--accent)]"
          id={`duration-${bound}-range`}
          max={bound === 'minimum' ? range.maximum : ONSET_AUDIT_DURATION_CEILING_SECONDS}
          min={bound === 'maximum' ? range.minimum : 0}
          onChange={(event) => updateFromSlider(event.currentTarget.valueAsNumber)}
          step={1}
          type="range"
          value={value}
        />
        <Input
          aria-invalid={Boolean(error)}
          aria-label={`${label} duration`}
          className="font-mono tabular-nums"
          maxLength={5}
          onBlur={() => {
            setDraft(formatDurationInput(value));
            setError(null);
          }}
          onChange={(event) => updateFromText(event.currentTarget.value)}
          value={draft}
        />
      </div>
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}

export function DurationRangeFilter({
  onChange,
  value,
}: {
  onChange: (range: DurationRange) => void;
  value: DurationRange;
}) {
  const defaultRange = {
    minimum: 0,
    maximum: ONSET_AUDIT_DURATION_CEILING_SECONDS,
  } as const;
  const active = value.minimum !== defaultRange.minimum || value.maximum !== defaultRange.maximum;

  return (
    <fieldset className="grid gap-3 border-0 p-0">
      <legend className="sr-only">Filter by duration</legend>
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-xs font-medium uppercase tracking-[0.1em] text-ink-3">
          Duration
        </span>
        {active ? (
          <Button onClick={() => onChange(defaultRange)} size="small" variant="ghost">
            Reset
          </Button>
        ) : null}
      </div>
      <div className="grid gap-4 rounded-sm border border-border bg-bg px-3 py-3 sm:grid-cols-2 sm:px-4">
        <DurationControl bound="minimum" onChange={onChange} range={value} />
        <DurationControl bound="maximum" onChange={onChange} range={value} />
      </div>
    </fieldset>
  );
}
